import * as angular from 'angular';
import * as _ from 'underscore';
import { IImgFileModel } from '../../../../../app.common/modules/common/directives/models';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { RetailerService } from '../../../../../app.retailer/modules/api/retailer.service';
import { HttpService } from '../../../api/http.service';
import { BlobService } from '../../services/blob.service';
import { DefinitionsService } from '../../services/definitions.service';
import { PopupService } from '../../services/popup.service';

interface IImage {
    token: string;
    url: string;
}

class WysiwygCarouselComponent {
    public static $inject = ['$q', '$timeout', 'http', 'blobService', 'retailerService', 'definitionsService', 'notificationService', 'popupService', '$translate'];
    public modalInstance: ng.ui.bootstrap.IModalServiceInstance = this as any;
    public images: IImage[] = [];
    public callbackUpdate: () => void;
    public uploadedImages: IImgFileModel[] = null;

    public waiting: boolean = false;
    public sortableOptions = {};
    public resolve: {
        images: string[],
        dataSource: any
    };
    private liveImagesTokens: string[] = [];
    private carouselElement: any;

    constructor(
        private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private http: HttpService,
        private blobService: BlobService,
        private retailerService: RetailerService,
        private definitionsService: DefinitionsService,
        private notificationService: NotificationService,
        private popupService: PopupService,
        private $translate: angular.translate.ITranslateService
    ) {
    }

    public $onInit() {
        if (!this.resolve.images) {
            this.resolve.images = [];
        }

        this.images = this.getCarouselImages(this.resolve.images);
        this.liveImagesTokens = _.pluck(this.images, 'token');
    }

    public cancel() {
        const toRemove = _.reject(this.images, (image) => {
            return _.contains(this.liveImagesTokens, image.token);
        });
        _.forEach(toRemove, (img) => {
            this.http.delete(this.retailerService.wysiwygImage(img.token));
        });
        this.modalInstance.dismiss({ $value: 'cancel' });
    }

    public save() {
        const tokens = _.pluck(this.images, 'token');
        const images = _.pluck(this.images, 'url');
        const toRemove = _.reject(this.liveImagesTokens, (token) => {
            return _.contains(tokens, token);
        });
        _.forEach(toRemove, (token) => {
            this.http.delete(this.retailerService.wysiwygImage(token));
        });

        this.modalInstance.close({ $value: images ? images : [] });
    }

    public deleteImage = (image: IImage, index: number) => {

        this.images = _.reject(this.images, (item) => {
            return item.token === image.token;
        });
        this.deleteUploadedImage(image.token);
    }

    public async uploadImages() {
        if (!this.uploadedImages) {
            return;
        }

        const failImages = _.reject(this.uploadedImages, this.emptyErrorImage);
        const validImages = _.filter(this.uploadedImages, this.emptyErrorImage);

        if (_.isEmpty(validImages)) {
            this.showFail(failImages);
            this.uploadedImages = null;
            return;
        }

        this.notificationService.showWaitingMessage();
        this.waiting = true;

        const promises: Array<Promise<string>> = [];
        _.forEach(validImages, (image) => {
            promises.push(this._uploadImage(image));
        });
        try {
            const urls = await this.$q.all(promises as Array<angular.IPromise<string>>);
            _.forEach(urls, (url) => {
                if (url && this.definitionsService.regexGuid.test(url)) {
                    this.images.push(
                        {
                            token: _.last(this.definitionsService.regexGuid.exec(url)),
                            url: url.toString()
                        }
                    );
                }
            });
            if (_.isEmpty(failImages)) {
                this.notificationService.showSuccessMessage();
            } else {
                this.showFail(failImages);
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }

        this.waiting = false;
        this.uploadedImages = null;
    }

    public isPristineModel() {
        if (_.size(this.images) !== _.size(this.liveImagesTokens)) {
            return false;
        } else {
            for (let c = 0; c < _.size(this.liveImagesTokens); c++) {
                if (this.images[c].token !== this.liveImagesTokens[c]) {
                    return false;
                }
            }
        }
        return true;
    }

    public delete() {
        const tokens = _.pluck(this.images, 'token');
        const toRemove = _.union(tokens, this.liveImagesTokens);

        _.forEach(toRemove, (token) => {
            this.http.delete(this.retailerService.wysiwygImage(token));
        });
        this.modalInstance.close({ $value: [] });
    }

    private async _uploadImage(file: IImgFileModel): Promise<string> {
        if (!file) {
            return this.$q.when(null as string);
        }
        const fd = new FormData();
        fd.append('FileImage', this.blobService.dataUriToBlob(file.dataUri), file.name);

        return this.http.post<string>(this.retailerService.wysiwygImage(), fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });
    }

    private async deleteUploadedImage(token: string) {
        if (!_.contains(this.liveImagesTokens, token)) {
            await this.http.delete(this.retailerService.wysiwygImage(token));
        }
    }

    private showFail = (failImages: IImgFileModel[]) => {
        this.notificationService.clearAll();
        if (_.size(failImages) === 0) {
            return;
        }
        let li = '';
        _.forEach(failImages, (image) => {
            li += '<li>' + image.name + '</li>';
        });
        const ul = '<ul class="pl15">' + li + '</ul>';
        this.popupService.alert({
            title: this.$translate.instant('cant_upload_photos') || 'Can\'t upload photos',
            content: `<p>${this.$translate.instant('cant_upload_photos_description')}</p>` + ul
        }).catch(() => { return; });
    }

    private emptyErrorImage(image: IImgFileModel) {
        return image.error == null || image.error === '';
    }

    private getCarouselImages(data: string[]): IImage[] {
        const images = [] as IImage[];
        _.forEach(data, (item) => {
            const match = item.match(this.definitionsService.regexGuid);
            if (!_.isEmpty(match)) {
                images.push(
                    {
                        token: _.last(match),
                        url: item
                    }
                );
            }
        });
        return images;
    }

}

angular.module('app.common').component('wysiwygCarousel', {
    template: require('./wysiwygCarousel.html'),
    controller: WysiwygCarouselComponent,
    controllerAs: 'vm',
    bindings: {
        resolve: '=',
        close: '&',
        dismiss: '&'
    }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.common/modules/common/components/wysiwygCarousel/wysiwygCarousel.component.ts